import React, { useMemo, useRef, useState } from "react";
import { Plus, Check, Search, Loader2 } from "lucide-react";

import TagChip from "./TagChip";
import { tagChipStyle } from "./tagIcons";

/**
 * Inline tag editor: shows the entity's tags as removable chips plus an
 * "Add tag" control that lists the workspace catalog and can create a new tag
 * on the fly.
 *
 *   <TagPicker
 *     catalog={allWorkspaceTags}
 *     value={selectedIds}
 *     onChange={(ids) => save(ids)}
 *     onCreateTag={(name) => Promise<tag>}
 *   />
 */
export default function TagPicker({
  catalog = [],
  value = [],
  onChange,
  onCreateTag,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const boxRef = useRef(null);

  const selected = useMemo(
    () => catalog.filter((t) => value.includes(t.id)),
    [catalog, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter((t) => !q || t.name.toLowerCase().includes(q));
  }, [catalog, query]);

  const exactExists = catalog.some(
    (t) => t.name.toLowerCase() === query.trim().toLowerCase(),
  );

  const toggle = (id) => {
    if (disabled) return;
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  const create = async () => {
    const name = query.trim();
    if (!name || !onCreateTag || creating) return;
    try {
      setCreating(true);
      const tag = await onCreateTag(name);
      if (tag?.id) {
        onChange([...value, tag.id]);
        setQuery("");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={boxRef} className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            onRemove={disabled ? undefined : () => toggle(tag.id)}
          />
        ))}

        {!disabled && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border-color px-2 py-0.5 text-[10px] font-semibold text-text-muted transition hover:border-primary/50 hover:text-primary dark:border-dark-border dark:text-dark-text-muted"
          >
            <Plus size={11} />
            {selected.length ? "Add" : "Add tag"}
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className="mt-2 rounded-xl border border-border-color bg-card-bg p-2 shadow-sm dark:border-dark-border dark:bg-dark-card">
          <div className="mb-1.5 flex items-center gap-1.5 rounded-lg border border-border-light px-2 dark:border-dark-border-light">
            <Search size={12} className="text-text-muted" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim() && !exactExists) create();
              }}
              placeholder="Search or create…"
              className="h-7 min-w-0 flex-1 bg-transparent text-[11px] text-text-primary outline-none placeholder:text-text-muted dark:text-dark-text-primary"
            />
          </div>

          <div className="max-h-40 space-y-0.5 overflow-y-auto">
            {filtered.map((tag) => {
              const on = value.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggle(tag.id)}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left transition hover:bg-theme-50 dark:hover:bg-dark-soft-blue"
                >
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                    style={tagChipStyle(tag.color)}
                  >
                    {tag.name}
                  </span>
                  {on && <Check size={13} className="text-primary dark:text-dark-primary" />}
                </button>
              );
            })}

            {query.trim() && !exactExists && onCreateTag && (
              <button
                type="button"
                onClick={create}
                disabled={creating}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-semibold text-primary transition hover:bg-theme-50 disabled:opacity-60 dark:text-dark-primary dark:hover:bg-dark-soft-blue"
              >
                {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                Create “{query.trim()}”
              </button>
            )}

            {filtered.length === 0 && !query.trim() && (
              <p className="px-2 py-1.5 text-[10px] text-text-muted dark:text-dark-text-muted">
                No tags yet.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
